// angular
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { isPlatformBrowser } from '@angular/common';
// services
import { StateManagementService } from './state-management.service';
import { StorageService } from '@app/shared/services/storage.service';
import { MessagingService } from './messaging.service';
import { SocketFactory } from '@app/shared/services/socket-factory.service';
// environment
import { environment } from '@environments/environment';
// interfaces & types
import { IChat } from '../interfaces';
import { IPublicChat } from '../interfaces/public-chat.interface';
import { SocketEventEnum } from '../enums/messaging-topic.enum';
import { AlertStatesEnum } from '../enums/alert-states.enum';

@Injectable()
export class ChatService {
  private _chatSocket: Socket | null = null;

  // dependencies
  private readonly _http = inject(HttpClient);
  private readonly _stateManagementService = inject(StateManagementService);
  private readonly _messagingService = inject(MessagingService);
  private readonly _storageService = inject(StorageService);
  private readonly _socketFactory = inject(SocketFactory);
  private readonly _platformId = inject(PLATFORM_ID);

  constructor() {
    this._connectToChatSocket();
  }

  private _connectToChatSocket() {
    const chatId = this._storageService.getChatId();

    if (isPlatformBrowser(this._platformId) && chatId) {
      this._chatSocket = this._socketFactory.getSocket('/chat');
    }
  }

  private _isChatSocketAvailable(): boolean {
    return !!this._chatSocket && !this._socketFactory.isPaused('/chat');
  }

  // create support chat
  private _createNewSupportChat(userData?: IPublicChat) {
    return userData
      ? this._http.post<{ chatId: string; memberId: string }>(
          'main/support/request-chat',
          userData
        )
      : this._http.post<{ chatId: string; memberId: string }>(
          'main/support/request-chat',
          null
        );
  }

  public createSupportChat(userData?: IPublicChat) {
    this._stateManagementService.isCreateChatLoading.set(true);

    this._createNewSupportChat(userData).subscribe({
      next: res => {
        if (res?.chatId && res?.memberId) {
          this._storageService.setChatId(res?.chatId);
          this._storageService.setMemberId(res?.memberId);
          this._stateManagementService.allMessages.set([]);
          this._stateManagementService.allMessagesCount.set(null);
          this._socketFactory.resetNamespace('/chat');
          this._socketFactory.resetNamespace('/message');
          this._chatSocket = null;
          this._connectToChatSocket();
          this._messagingService.reconnectMessageSocket();
          this._stateManagementService.chatAlertState.set(
            AlertStatesEnum.supportChat
          );
          this._stateManagementService.isMessagesLoading.set(true);
          this.getChatByChatId(res?.chatId);
          this._messagingService.getAllMessages(res?.chatId);
        }
      },
      error: error => {
        console.error(error);
        this._stateManagementService.isCreateChatLoading.set(false);
      },
      complete: () => {
        this._stateManagementService.isCreateChatLoading.set(false);
      },
    });
  }

  // get chat by chat id
  public getChatByChatId(chat_id: string) {
    this._connectToChatSocket();
    if (!this._isChatSocketAvailable()) {
      this._stateManagementService.isChatLoading.set(false);
      return;
    }

    this._stateManagementService.isChatLoading.set(true);

    this._chatSocket!.ioSocket.emit(
      SocketEventEnum.findOneChat,
      { chat_id },
      (res: IChat) => {
        const data = res;

        try {
          if (data) {
            this._stateManagementService.oneChatById.set(data);
          }

          this._stateManagementService.isChatLoading.set(false);
        } catch (error) {
          this._stateManagementService.isError.set(true);
          this._stateManagementService.errorMessage.set(error);
          this._stateManagementService.isChatExist.set(false);
        } finally {
          this._stateManagementService.isChatLoading.set(false);
        }
      }
    );
  }

  // update chat image api
  public updateChatImage(photoUrl: string, id: string) {
    //   .mutate<OneChatResponse>({
    //     mutation: _updateChatImageSchema,
    //     variables: {
    //       photoUrl,
    //       id,
    //     },
    //   })
    //   .subscribe({
    //     next: ({ data }) => {},
    //     error: errors => {
    //       this._stateManagementService.isError.set(true);
    //       this._stateManagementService.errorMessage.set(errors);
    //     },
    //     complete: () => {},
    //   });
  }

  /////////////////////// chat socket methods //////////////////////////////////

  // get new chat result in socket
  public createNewChatWithSocket(chat: IChat) {
    const imgBaseUrl = environment.baseUrl.messenger;
    const pictureSrc = chat.photo_url;
    const editedData = {
      ...chat,
      picture: {
        src:
          !!pictureSrc && pictureSrc !== ''
            ? imgBaseUrl + pictureSrc
            : 'assets/images/messenger/profile.png',
        alt: chat.title,
      },
    };

    !!editedData && this._stateManagementService.oneChatById.set(editedData);
  }

  // update chat on new member added result in socket
  public addMemberToChatWithSocket(chat: IChat) {
    this._stateManagementService.oneChatById.set(chat);
  }

  // update chat on remove a member result in socket
  public removeMemberFromChatWithSocket(chat: IChat) {
    this._stateManagementService.oneChatById.set(chat);
  }
}
